
import { useState, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import SellerCard from '@/components/SellerCard';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Loader2 } from "lucide-react";
import { fetchSellerProfiles } from "@/lib/api/profiles/queries";
import { getSupabaseClient } from "@/integrations/supabase/client";
import { SellerProfile } from '@/types/profiles';

interface SellerData extends SellerProfile {
  offerCount: number;
  rating: number;
  reviewCount: number;
  tags: string[];
}

const Sellers = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('rating');

  const { data: sellers = [], isLoading, error } = useQuery({
    queryKey: ['sellers'],
    queryFn: async () => {
      try {
        // Get basic seller profiles
        const sellerProfiles = await fetchSellerProfiles({
          sortBy: 'created_at',
          order: 'desc'
        });

        const supabaseClient = getSupabaseClient();
        
        // Fetch additional metrics for each seller
        const enrichedSellers = await Promise.all(sellerProfiles.map(async (profile) => {
          // Get offer count
          const { data: offers, error: offersError } = await supabaseClient
            .from('offers')
            .select('id')
            .eq('seller_profile_id', profile.id)
            .eq('is_active', true);

          if (offersError) {
            console.error('Error fetching offers:', offersError);
            return null;
          }

          // Get ratings
          const { data: reviews, error: reviewsError } = await supabaseClient.rpc(
            'get_seller_reviews',
            { seller_id: profile.id }
          );

          if (reviewsError) {
            console.error('Error fetching reviews:', reviewsError);
            return null;
          }

          // Calculate average rating
          const totalRating = reviews?.reduce((sum: number, review: any) => sum + review.rating, 0) || 0;
          const averageRating = reviews?.length > 0 ? totalRating / reviews.length : 0;

          // Return enriched seller data
          return {
            ...profile,
            offerCount: offers?.length || 0,
            rating: parseFloat(averageRating.toFixed(1)),
            reviewCount: reviews?.length || 0,
            tags: ['SEO', 'Content', 'Marketing'] // Example tags - ideally these would come from the database
          } as SellerData;
        }));

        return enrichedSellers.filter(Boolean) as SellerData[];
      } catch (err) {
        console.error('Error loading sellers:', err);
        throw err;
      }
    }
  });

  const filteredSellers = sellers?.filter(seller => 
    seller?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Sort the sellers based on the selected sort order
  const sortedSellers = [...filteredSellers].sort((a, b) => {
    if (sortOrder === 'rating') {
      return b.rating - a.rating;
    } else if (sortOrder === 'offers') {
      return b.offerCount - a.offerCount;
    }
    return 0;
  });

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-2xl font-bold mb-4">Browse Sellers</h1>

      <div className="flex flex-col md:flex-row items-center justify-between mb-6">
        <div className="flex items-center mb-2 md:mb-0">
          <Label htmlFor="search" className="mr-2 hidden md:block">Search:</Label>
          <Input
            type="text"
            id="search"
            placeholder="Search by name"
            className="md:w-64"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex items-center">
          <Label htmlFor="sort" className="mr-2 hidden md:block">Sort by:</Label>
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="md:w-56">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating">Rating</SelectItem>
              <SelectItem value="offers">Number of Offers</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-10 w-10 animate-spin text-brand-blue" />
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">Failed to load sellers</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      ) : sortedSellers.length === 0 ? (
        <div className="text-center text-gray-500 py-12">No sellers found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedSellers.map((seller) => (
            <SellerCard
              key={seller.id}
              id={seller.id}
              name={seller.name}
              bio={seller.bio || "No bio available"}
              logoUrl={seller.logo_url || "/placeholder.svg"}
              verified={seller.verified}
              offerCount={seller.offerCount}
              rating={seller.rating}
              reviewCount={seller.reviewCount}
              tags={seller.tags}
              slug={seller.slug || undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Sellers;
